import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "../lib/icons";
import { Btn, LivenessPill, Pill, PersonaPill, ResultPill, FactorChip, Modal } from "../lib/primitives";
import { fmt } from "../lib/format";
import { useDashboardUsers, useUserAuditLog, useUserStats } from "../lib/hooks";
import type { DashboardUserRow } from "../lib/api";
import { classifyLiveness } from "../lib/liveness";

/** Compact relative time for the "Last auth" column. */
function relTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Adapt the auth-server's user row shape to the design's row shape so the
 * existing JSX doesn't need to know which one it's rendering.
 */
function adaptUserRow(r: DashboardUserRow) {
  const enrolledAt = new Date(r.createdAt);
  return {
    // The provider's own user id keys everything client-side; the global
    // persona id never reaches the SPA (authnc-admin#76).
    id: r.externalUserId,
    externalId: r.externalUserId,
    providerSubject: r.providerSubject,
    label: r.externalUserId,
    persona: (r.personaType === "agent" ? "agent" : "human") as "human" | "agent",
    enrolledAt,
    enrolledLabel: enrolledAt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    lastAuthAt: r.lastAuthAt,
    lastAuthLabel: r.lastAuthAt ? relTime(r.lastAuthAt) : "–",
    authCount: r.authCount,
    // V1 has a single scheme (passkey_question_v1); a current enrollment
    // attests exactly these two factors.
    factors: r.enrolledFactors > 0 ? ["image_points", "passkey"] : [],
    status: (r.personaStatus === "active" ? "active" : "deactivated") as
      | "active"
      | "deactivated",
  };
}

const SCREEN_TO_PATH: Record<string, string> = {
  dashboard: "/",
  quickstart: "/quickstart",
  logs: "/logs",
  users: "/users",
  keys: "/keys",
  policy: "/policy",
  branding: "/sign-in-page",
  hosted: "/sign-in-page",
  team: "/team",
  billing: "/billing",
  settings: "/settings",
};

function MiniStat({ label, value, sub }: { label: React.ReactNode; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="ad-card" style={{ padding: 16 }}>
      <div className="ad-eyebrow-sm">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.02em", margin: "6px 0 2px", fontVariantNumeric: "tabular-nums", color: "var(--ad-fg-1)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>{sub}</div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: React.ReactNode; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="ad-field">
      <div className="ad-field-label">{label}</div>
      <div style={{ fontSize: mono ? 12 : 13, fontFamily: mono ? "var(--rb-font-mono)" : "inherit", color: "var(--ad-fg-1)" }}>{value}</div>
    </div>
  );
}

function UserDetailDrawer({ user, onClose, onNav: _onNav }: { user: any; onClose: () => void; onNav: (id: string) => void }) {
  // Real per-account audit history. The factor column stays thin until the
  // auth-server enriches verification.* metadata (auth-server#82).
  const audit = useUserAuditLog(user?.externalId);
  const history = useMemo(() => {
    return (audit.data ?? []).map((e) => {
      const meta = (e.metadata ?? {}) as Record<string, unknown>;
      const factors = Array.isArray(meta.factors) ? (meta.factors as string[]) : [];
      return {
        id: e.id,
        ts: new Date(e.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }),
        factors,
        livenessState: classifyLiveness(e).state,
        result:
          e.action === "verification.success"
            ? "success"
            : e.action === "verification.failed"
              ? "fail-factor"
              : e.action,
      };
    });
  }, [audit.data]);

  if (!user) return null;

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      eyebrow="Person"
      title={user.label}
      width={760}
      footer={<Btn kind="ghost" onClick={onClose}>Close</Btn>}
    >
      <div className="ad-stack-lg">
        <div className="ad-grid-2">
          <Detail label="External user ID" value={user.externalId} mono />
          <Detail label="Provider subject" value={user.providerSubject ?? "–"} mono />
          <Detail label="Who" value={<PersonaPill persona={user.persona} />} />
          <Detail label="Status" value={user.status === "deactivated" ? <Pill kind="error" dot>Deactivated</Pill> : <Pill kind="success" dot>Active</Pill>} />
          <Detail label="Set up" value={user.enrolledLabel} />
          <Detail label="Last sign-in" value={user.lastAuthLabel} />
          <Detail label="Sign-ins" value={fmt(user.authCount)} mono />
        </div>

        <div>
          <div className="ad-eyebrow-sm" style={{ marginBottom: 8 }}>Methods set up</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{user.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div>
          <div style={{ fontSize: 11, color: "var(--ad-fg-3)", marginTop: 8, lineHeight: 1.6 }}>
            Factor contents are stored only on the user's device. Neither your team nor AuthenSee can view or export them.
            Users manage their own factors in the AuthenSee app.
          </div>
        </div>

        <div>
          <div className="ad-eyebrow-sm" style={{ marginBottom: 8 }}>Recent sign-ins</div>
          {audit.isLoading ? (
            <div style={{ fontSize: 12, color: "var(--ad-fg-3)", padding: "12px 0" }}>Loading…</div>
          ) : history.length === 0 ? (
            <div className="ad-empty" style={{ border: 0, margin: 0 }}>
              <div className="ad-empty-title">No sign-ins yet</div>
              <div className="ad-empty-sub">This person hasn't signed in since setting up.</div>
            </div>
          ) : (
            <table className="ad-table">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>When</th>
                  <th>Method</th>
                  <th>Result</th>
                  <th>Liveness</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h: any) => (
                  <tr key={h.id}>
                    <td className="mono">{h.ts}</td>
                    <td><div style={{ display: "flex", gap: 4 }}>{h.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div></td>
                    <td><ResultPill result={h.result} /></td>
                    <td><LivenessPill state={h.livenessState} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function Users() {
  const navigate = useNavigate();
  const onNav = (id: string) => navigate(SCREEN_TO_PATH[id] ?? "/");

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>(null);
  const [persona, setPersona] = useState("all");

  const { data: serverUsers, isLoading } = useDashboardUsers({ limit: 100 });
  const { data: stats } = useUserStats();
  const rows = (serverUsers ?? []).map(adaptUserRow);

  const filtered = useMemo(
    () =>
      rows.filter((u: any) => {
        if (persona !== "all" && u.persona !== persona) return false;
        if (
          query &&
          !u.externalId.toLowerCase().includes(query.toLowerCase()) &&
          !u.label.toLowerCase().includes(query.toLowerCase())
        )
          return false;
        return true;
      }),
    [query, persona, rows],
  );

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Monitor</div>
          <h1 className="ad-h1">People</h1>
          <div className="ad-sub">
            Everyone who set up AuthenSee in your app. We keep your external user ID for them and a
            sealed fingerprint of their sign-in, never the sign-in itself.
          </div>
        </div>
      </div>

      <div className="ad-grid-4" style={{ gap: 14, marginBottom: 18 }}>
        <MiniStat label="Set up" value={stats ? fmt(stats.enrolled) : "…"} sub="people in total" />
        <MiniStat label="Active this week" value={stats ? fmt(stats.active7d) : "…"} sub="signed in, last 7 days" />
        <MiniStat label="New this week" value={stats ? fmt(stats.new7d) : "…"} sub="set up, last 7 days" />
        <MiniStat label="AI agents" value={stats ? fmt(stats.agentPersonas) : "…"} sub="detected automatically" />
      </div>

      <div className="ad-filterbar">
        <div className="ad-top-search" style={{ flex: "0 1 320px", border: "1px solid var(--ad-hairline)" }}>
          <Icons.Search size={14} />
          <input placeholder="Search by external user ID or name" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={{ width: 1, height: 22, background: "var(--ad-hairline)" }} />
        <span className="ad-eyebrow-sm">Who</span>
        <button className="ad-filter-chip" data-active={persona === "all"} onClick={() => setPersona("all")}>All</button>
        <button className="ad-filter-chip" data-active={persona === "human"} onClick={() => setPersona("human")}>People</button>
        <button className="ad-filter-chip" data-active={persona === "agent"} onClick={() => setPersona("agent")}>AI agents</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "var(--ad-fg-3)", fontFamily: "var(--rb-font-mono)" }}>{fmt(filtered.length)} users</span>
      </div>

      {isLoading ? (
        <div className="ad-card-flat" style={{ padding: 32, textAlign: "center", color: "var(--ad-fg-3)", fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="ad-card-flat">
          <div className="ad-empty" style={{ border: 0, margin: 0 }}>
            <div className="ad-empty-title">No one yet</div>
            <div className="ad-empty-sub">People show up here once they set up AuthenSee in your app.</div>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ad-card-flat">
          <div className="ad-empty" style={{ border: 0, margin: 0 }}>
            <div className="ad-empty-title">No one matches</div>
            <div className="ad-empty-sub">Try another search or filter.</div>
          </div>
        </div>
      ) : (
        <div className="ad-card-flat" style={{ padding: 0, overflowX: "auto" }}>
        <table className="ad-table">
          <thead>
            <tr>
              <th>External user ID</th>
              <th>Name</th>
              <th>Method</th>
              <th>Who</th>
              <th>Set up</th>
              <th>Last sign-in</th>
              <th style={{ textAlign: "right" }}>Sign-ins</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 40).map((u: any) => (
              <tr key={u.id} className="is-selectable" onClick={() => setSelected(u)}>
                <td className="mono">{u.externalId}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 999, background: "var(--ad-surface-3)", color: "var(--ad-fg-2)", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700, letterSpacing: 0.1 }}>
                      {u.label.split(" ").map((s: string) => s[0]).join("")}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{u.label}</div>
                      {u.providerSubject && (
                        <div style={{ fontSize: 10, color: "var(--ad-fg-3)", fontFamily: "var(--rb-font-mono)" }}>{u.providerSubject}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{u.factors.map((f: string) => <FactorChip key={f} name={f} />)}</div></td>
                <td><PersonaPill persona={u.persona} /></td>
                <td className="muted" style={{ fontSize: 12 }}>{u.enrolledLabel}</td>
                <td className="muted" style={{ fontSize: 12 }}>{u.lastAuthLabel}</td>
                <td className="num">{fmt(u.authCount)}</td>
                <td>{u.status === "deactivated" ? <Pill kind="error" dot>Deactivated</Pill> : <Icons.ChevronR size={14} className="row-arrow" />}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <UserDetailDrawer user={selected} onClose={() => setSelected(null)} onNav={onNav} />
    </>
  );
}
